import { debounce } from '@theme/utilities';

/** Only released after this long without playback, so blocked autoplay can't hide the content for good. */
const AUTOPLAY_GRACE_MS = 10000;

/**
 * Drives the hero content fade off the hero video's own clock.
 *
 * The cycle is a CSS animation whose keyframe stops are generated from `video.duration`
 * (see custom-hero.liquid), so the shape of it is already correct without JS. What JS adds is the
 * clock: a CSS animation starts at page parse, whereas the hold is meant to count from the moment
 * the video actually renders frames. So the animation is held at 0 until the video is playing,
 * resumed and paused alongside it (buffering included), and re-synced to `currentTime` on every
 * update — which also absorbs the drift each native loop restart would otherwise accumulate.
 *
 * The editor runs the same cycle as the storefront, so the timing can be judged where it is set.
 * Nothing about selection or editing suspends it: each settings change re-renders the section, which
 * restarts the video and this element together, so the preview replays from the top of the loop.
 */
class CustomHeroTiming extends HTMLElement {
  connectedCallback() {
    this.#attach();
    this.#resizeListener = debounce(() => this.#attach(), 200);
    window.addEventListener('resize', this.#resizeListener);

    // The theme editor re-renders the section on every settings change and connects us before layout
    // has settled, so the first pass can misread which video is on screen. One more pass a frame
    // later costs nothing and early-returns when the answer hasn't changed.
    this.#settleFrame = requestAnimationFrame(() => this.#attach());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    cancelAnimationFrame(this.#settleFrame);
    this.#detach();
  }

  /** @type {() => void} */
  #resizeListener;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {number | undefined} */
  #graceTimer;

  /** @type {number | undefined} */
  #settleFrame;

  /** Desktop and mobile media are swapped with `display: none`, so pick whichever is on screen. */
  #attach() {
    const section = this.closest('.custom-hero');
    // Scoped to the media grid so a video inside a content block can't be mistaken for the hero video.
    const videos = /** @type {HTMLVideoElement[]} */ (
      Array.from(section?.querySelectorAll('.custom-hero__media-grid video') ?? [])
    );
    // The fallback matters: `offsetParent` is null for every video while the section sits in a
    // not-yet-laid-out container, and treating that as "no hero video" would release the content
    // permanently — the exact bug where the text shows immediately in the theme editor.
    const video = videos.find((candidate) => candidate.offsetParent !== null) ?? videos[0] ?? null;

    if (video === this.#video) return;

    this.#detach();

    // No hero video means no logo to wait for: leave the content up.
    if (!video) {
      this.dataset.timing = 'off';
      return;
    }

    this.#video = video;
    video.addEventListener('playing', this.#resume);
    video.addEventListener('timeupdate', this.#sync);
    video.addEventListener('waiting', this.#suspend);
    video.addEventListener('stalled', this.#suspend);
    video.addEventListener('pause', this.#suspend);

    if (this.#isPlaying(video)) {
      this.#resume();
    } else {
      // Park the cycle at the top of the loop so a slow load can't spend the hold before the
      // logo has been seen. `playing` releases it.
      this.#suspend();
      this.#seek(0);
      this.#graceTimer = window.setTimeout(() => {
        this.dataset.timing = 'off';
      }, AUTOPLAY_GRACE_MS);
    }
  }

  #detach() {
    window.clearTimeout(this.#graceTimer);
    delete this.dataset.timing;

    const video = this.#video;
    if (!video) return;

    video.removeEventListener('playing', this.#resume);
    video.removeEventListener('timeupdate', this.#sync);
    video.removeEventListener('waiting', this.#suspend);
    video.removeEventListener('stalled', this.#suspend);
    video.removeEventListener('pause', this.#suspend);
    this.#video = null;

    for (const animation of this.#animations()) {
      animation.play();
    }
  }

  /** Not just `!paused` — a video that is buffering is unpaused but isn't showing anything yet. */
  #isPlaying(video) {
    return !video.paused && !video.ended && video.readyState >= video.HAVE_FUTURE_DATA;
  }

  /** Only the content wrapper is animated — descendants are deliberately left alone. */
  #animations() {
    return this.querySelector('.custom-hero__content-wrapper')?.getAnimations() ?? [];
  }

  #seek(time) {
    for (const animation of this.#animations()) {
      animation.currentTime = time;
    }
  }

  #sync = () => {
    const video = this.#video;
    if (!video || !Number.isFinite(video.duration)) return;

    this.#seek(video.currentTime * 1000);
  };

  #resume = () => {
    // Playback started after all, so the grace release is moot — take the cycle back.
    window.clearTimeout(this.#graceTimer);
    delete this.dataset.timing;

    this.#sync();
    for (const animation of this.#animations()) {
      animation.play();
    }
  };

  #suspend = () => {
    for (const animation of this.#animations()) {
      animation.pause();
    }
  };
}

if (!customElements.get('custom-hero-timing')) {
  customElements.define('custom-hero-timing', CustomHeroTiming);
}
